import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { SubscriptionApi } from "../api/subscription_api";
import {
  buildCreatePlanPayload,
  buildCreateVersionPayload,
  buildUpdatePlanPayload,
  CreatePlanFormData,
  EditPlanFormData,
  EditVersionFormData,
  getErrorMessage,
  initialCreatePlanForm,
  initialEditPlanForm,
  initialEditVersionForm,
  isScheduledVersion,
  mapTierToEditForm,
  normalizeTierVersions,
  Subscription,
  SubscriptionSummary,
  SubscriptionTab,
  TierDetails,
  TierPricingDetails,
  ToastState,
} from "../model/subscription_model";

type EditableSubscription = Pick<Subscription, "id" | "name">;

export const useSubscriptionsViewModel = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubscription, setEditingSubscription] =
    useState<EditableSubscription | null>(null);
  const [createPlanForm, setCreatePlanForm] =
    useState<CreatePlanFormData>(initialCreatePlanForm);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [editPlanForm, setEditPlanForm] =
    useState<EditPlanFormData>(initialEditPlanForm);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [viewTierDetails, setViewTierDetails] = useState<TierDetails | null>(
    null,
  );
  const [viewTierVersions, setViewTierVersions] = useState<
    TierPricingDetails[]
  >([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [editVersionForm, setEditVersionForm] =
    useState<EditVersionFormData>(initialEditVersionForm);
  const [editVersionSubmitting, setEditVersionSubmitting] = useState(false);
  const [editVersionError, setEditVersionError] = useState<string | null>(null);
  const [editVersionSuccess, setEditVersionSuccess] = useState<string | null>(
    null,
  );
  const [deletingVersionId, setDeletingVersionId] = useState<string | null>(
    null,
  );
  const [versionActionError, setVersionActionError] = useState<string | null>(
    null,
  );
  const [versionPendingDelete, setVersionPendingDelete] =
    useState<TierPricingDetails | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [activeTab, setActiveTab] = useState<SubscriptionTab>("plans");

  const subscriptionTierOptions = useMemo(
    () =>
      subscriptions.map((subscription) => ({
        id: subscription.id,
        name: subscription.name,
      })),
    [subscriptions],
  );

  const closeToast = useCallback(() => {
    setToast((current) =>
      current ? { ...current, isVisible: false } : null,
    );
  }, []);

  const showToast = useCallback(
    (
      message: string,
      type: ToastState["type"] = "success",
    ) => {
      setToast({
        message,
        type,
        isVisible: true,
      });
    },
    [],
  );

  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("No access token found");
      }

      const data = await SubscriptionApi.getPricingPlans();
      console.log("Subscriptions data:", data);

      setSubscriptions(data.plans || []);
      setSummary(data.summary || null);
    } catch (requestError) {
      console.error("Error loading subscriptions:", requestError);
      setError(
        getErrorMessage(requestError, "Failed to load subscriptions"),
      );
      setSubscriptions([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSubscriptions();
  }, [loadSubscriptions]);

  const refreshViewedPlan = useCallback(async (tierId: string) => {
    const [details, versions] = await Promise.all([
      SubscriptionApi.getTier(tierId),
      SubscriptionApi.getTierVersions(tierId),
    ]);
    setViewTierDetails(details);
    setViewTierVersions(normalizeTierVersions(versions));
  }, []);

  const handleViewPlan = useCallback(async (subscription: Subscription) => {
    setViewTierDetails(null);
    setViewTierVersions([]);
    setViewError(null);
    setVersionActionError(null);
    setDeletingVersionId(null);
    setVersionPendingDelete(null);
    setViewLoading(true);

    try {
      const [details, versions] = await Promise.all([
        SubscriptionApi.getTier(subscription.id),
        SubscriptionApi.getTierVersions(subscription.id),
      ]);
      setViewTierDetails(details);
      setViewTierVersions(normalizeTierVersions(versions));
    } catch (requestError) {
      console.error("Error loading tier details:", requestError);
      setViewError(
        getErrorMessage(requestError, "Failed to load tier details"),
      );
    } finally {
      setViewLoading(false);
    }
  }, []);

  const closeViewModal = useCallback(() => {
    setViewTierDetails(null);
    setViewTierVersions([]);
    setViewError(null);
    setVersionActionError(null);
    setDeletingVersionId(null);
    setVersionPendingDelete(null);
    setViewLoading(false);
  }, []);

  const openEditPlan = useCallback(
    async (subscription: EditableSubscription) => {
      setEditingSubscription(subscription);
      setShowCreateModal(false);
      setEditPlanForm(initialEditPlanForm);
      setEditError(null);
      setEditLoading(true);

      try {
        const tier = await SubscriptionApi.getTier(subscription.id);
        setEditPlanForm(mapTierToEditForm(tier));
      } catch (requestError) {
        console.error("Error loading subscription plan for edit:", requestError);
        setEditError(
          getErrorMessage(requestError, "Failed to load subscription plan"),
        );
      } finally {
        setEditLoading(false);
      }
    },
    [],
  );

  const closeEditModal = useCallback(() => {
    setEditingSubscription(null);
    setEditPlanForm(initialEditPlanForm);
    setEditError(null);
    setEditLoading(false);
    setEditSubmitting(false);
  }, []);

  const handleEditPlanChange = useCallback(
    (field: keyof EditPlanFormData, value: string | boolean | number) => {
      setEditPlanForm((current) => ({
        ...current,
        [field]: value,
      }));
    },
    [],
  );

  const handleEditPlanSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!editingSubscription) {
        return;
      }

      setEditSubmitting(true);
      setEditError(null);

      try {
        await SubscriptionApi.updateTier(
          editingSubscription.id,
          buildUpdatePlanPayload(editPlanForm),
        );
        await loadSubscriptions();
        closeEditModal();
        showToast("Subscription plan updated successfully.", "success");
      } catch (requestError) {
        console.error("Error updating subscription plan:", requestError);
        setEditError(
          getErrorMessage(requestError, "Failed to update subscription plan"),
        );
      } finally {
        setEditSubmitting(false);
      }
    },
    [
      closeEditModal,
      editPlanForm,
      editingSubscription,
      loadSubscriptions,
      showToast,
    ],
  );

  const resetCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setCreatePlanForm(initialCreatePlanForm);
    setCreateError(null);
    setCreateSuccess(null);
    setCreateSubmitting(false);
  }, []);

  const openCreatePlanModal = useCallback(() => {
    setCreatePlanForm(initialCreatePlanForm);
    setCreateError(null);
    setCreateSuccess(null);
    setShowCreateModal(true);
  }, []);

  const handleCreatePlanChange = useCallback(
    (field: keyof CreatePlanFormData, value: string | boolean | number) => {
      setCreatePlanForm((current) => ({
        ...current,
        [field]: value,
      }));
    },
    [],
  );

  const handleCreatePlanSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setCreateSubmitting(true);
      setCreateError(null);
      setCreateSuccess(null);

      try {
        await SubscriptionApi.createTier(
          buildCreatePlanPayload(createPlanForm),
        );
        setCreateSuccess("Subscription plan created successfully.");
        setCreatePlanForm(initialCreatePlanForm);
        await loadSubscriptions();
      } catch (requestError) {
        console.error("Error creating subscription plan:", requestError);
        setCreateError(
          getErrorMessage(requestError, "Failed to create subscription plan"),
        );
      } finally {
        setCreateSubmitting(false);
      }
    },
    [createPlanForm, loadSubscriptions],
  );

  const openAddVersionModal = useCallback(() => {
    setShowAddVersionModal(true);
    setEditVersionForm(initialEditVersionForm);
    setEditVersionError(null);
    setEditVersionSuccess(null);
  }, []);

  const closeAddVersionModal = useCallback(() => {
    setShowAddVersionModal(false);
    setEditVersionForm(initialEditVersionForm);
    setEditVersionError(null);
    setEditVersionSuccess(null);
    setEditVersionSubmitting(false);
  }, []);

  const handleEditVersionChange = useCallback(
    (field: keyof EditVersionFormData, value: string) => {
      setEditVersionForm((current) => ({
        ...current,
        [field]: value,
      }));
    },
    [],
  );

  const handleAddVersionSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!viewTierDetails?.id) {
        return;
      }

      setEditVersionSubmitting(true);
      setEditVersionError(null);
      setEditVersionSuccess(null);

      try {
        await SubscriptionApi.createTierVersion(
          viewTierDetails.id,
          buildCreateVersionPayload(editVersionForm),
        );
        await refreshViewedPlan(viewTierDetails.id);
        setEditVersionSuccess("New version created successfully.");
        setTimeout(closeAddVersionModal, 500);
      } catch (requestError) {
        console.error("Error creating tier version:", requestError);
        setEditVersionError(
          getErrorMessage(requestError, "Failed to create tier version"),
        );
      } finally {
        setEditVersionSubmitting(false);
      }
    },
    [
      closeAddVersionModal,
      editVersionForm,
      refreshViewedPlan,
      viewTierDetails,
    ],
  );

  const openDeleteScheduledVersionConfirm = useCallback(
    (version: TierPricingDetails) => {
      if (!version.id || !isScheduledVersion(version)) {
        return;
      }

      setVersionActionError(null);
      setVersionPendingDelete(version);
    },
    [],
  );

  const closeDeleteScheduledVersionConfirm = useCallback(() => {
    if (deletingVersionId) {
      return;
    }

    setVersionPendingDelete(null);
  }, [deletingVersionId]);

  const handleDeleteScheduledVersion = useCallback(async () => {
    const version = versionPendingDelete;

    if (
      !viewTierDetails?.id ||
      !version ||
      !version.id ||
      !isScheduledVersion(version)
    ) {
      return;
    }

    setDeletingVersionId(version.id);
    setVersionActionError(null);

    try {
      await SubscriptionApi.deleteTierVersion(version.id);
      await refreshViewedPlan(viewTierDetails.id);
      setVersionPendingDelete(null);
    } catch (requestError) {
      console.error("Error deleting scheduled tier version:", requestError);
      setVersionActionError(
        getErrorMessage(requestError, "Failed to delete scheduled version"),
      );
    } finally {
      setDeletingVersionId(null);
    }
  }, [refreshViewedPlan, versionPendingDelete, viewTierDetails]);

  return {
    activeTab,
    closeAddVersionModal,
    closeDeleteScheduledVersionConfirm,
    closeEditModal,
    closeToast,
    closeViewModal,
    createError,
    createPlanForm,
    createSubmitting,
    createSuccess,
    deletingVersionId,
    editError,
    editLoading,
    editPlanForm,
    editSubmitting,
    editingSubscription,
    editVersionError,
    editVersionForm,
    editVersionSubmitting,
    editVersionSuccess,
    error,
    handleAddVersionSubmit,
    handleCreatePlanChange,
    handleCreatePlanSubmit,
    handleDeleteScheduledVersion,
    handleEditPlanChange,
    handleEditPlanSubmit,
    handleEditVersionChange,
    handleViewPlan,
    isScheduledVersion,
    loadSubscriptions,
    loading,
    openAddVersionModal,
    openCreatePlanModal,
    openDeleteScheduledVersionConfirm,
    openEditPlan,
    resetCreateModal,
    setActiveTab,
    showAddVersionModal,
    showCreateModal,
    showToast,
    subscriptions,
    subscriptionTierOptions,
    summary,
    toast,
    versionActionError,
    versionPendingDelete,
    viewError,
    viewLoading,
    viewTierDetails,
    viewTierVersions,
  };
};
